import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  FaShoppingCart,
  FaCheckCircle,
  FaSpinner,
  FaMobileAlt,
  FaTshirt,
  FaMinus,
  FaPlus,
} from 'react-icons/fa';
import { GiBilledCap } from 'react-icons/gi';
import { PRODUCTS, CATEGORY_LABELS } from '../../data/products';
import './Shop.css';

// Accent color used for the stripe on each product card + the order preview.
// Add an entry here if you introduce a product with a new color.
const COLOR_HEX = {
  Red: '#C62828',
  Green: '#2E7D32',
  White: '#FFFFFF',
};

// Small icon shown on each product card / the order preview to signal type.
// Add an entry here if you introduce a new category.
const CATEGORY_ICON = {
  shirt: FaTshirt,
  cap: GiBilledCap,
};

const EMPTY = {
  fullName: '',
  phone: '',
  email: '',
  deliveryAddress: '',
  notes: '',
  honeypot: '',
};

function makeOrderRef() {
  return `NSK-SHOP-${Date.now().toString().slice(-6)}`;
}

function validate(fields, size, hasSizes) {
  const errs = {};
  if (!fields.fullName.trim()) errs.fullName = 'Full name is required.';
  if (!fields.phone.trim()) errs.phone = 'Phone number is required.';
  if (!fields.email.trim()) errs.email = 'Email address is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) errs.email = 'Enter a valid email address.';
  if (!fields.deliveryAddress.trim()) errs.deliveryAddress = 'Delivery address is required.';
  if (hasSizes && !size) errs.size = 'Please select a size.';
  return errs;
}

/* ── Success State ── */
function SuccessState({ orderRef, totalAmount, onReset }) {
  return (
    <div className="shop-success" role="alert" aria-live="polite">
      <div className="shop-success__icon" aria-hidden="true">
        <FaCheckCircle size={42} />
      </div>
      <h2 className="shop-success__title">Order Received</h2>
      <p className="shop-success__text">
        Your order has been recorded. Complete payment using the details
        below to have it processed and shipped.
      </p>

      <div className="shop-payment-box">
        <div className="shop-payment-box__header">
          <FaMobileAlt size={20} aria-hidden="true" />
          <span>Pay via M-Pesa Paybill</span>
        </div>
        <div className="shop-payment-box__row">
          <span>Paybill Number</span>
          <strong>522533</strong>
        </div>
        <div className="shop-payment-box__row">
          <span>Account Number</span>
          <strong>{orderRef}</strong>
        </div>
        <div className="shop-payment-box__row">
          <span>Amount</span>
          <strong>KES {totalAmount.toLocaleString()}</strong>
        </div>
        <p className="shop-payment-box__note">
          Use <strong>{orderRef}</strong> exactly as your account number so we
          can match your payment to your order. After paying, email your
          M-Pesa confirmation to{' '}
          <a href="mailto:info@nuclearsocietyofkenya.org">
            info@nuclearsocietyofkenya.org
          </a>{' '}
          with subject <strong>"Shop Order — {orderRef}"</strong>.
        </p>
      </div>

      <div className="shop-success__actions">
        <Link to="/" className="btn btn--green">Back to Home</Link>
        <button
          className="btn btn--outline"
          onClick={onReset}
          style={{ color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
        >
          Place Another Order
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   SHOP PAGE
══════════════════════════════════════════ */
function Shop() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProductId, setSelectedProductId] = useState(PRODUCTS[0].id);
  const [size, setSize] = useState(PRODUCTS[0].hasSizes ? PRODUCTS[0].sizes[0] : null);
  const [quantity, setQuantity] = useState(1);

  const [fields, setFields] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [orderRef, setOrderRef] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);

  const formRef = useRef(null);
  const selectedProduct = PRODUCTS.find(p => p.id === selectedProductId);

  const categories = useMemo(
    () => ['all', ...new Set(PRODUCTS.map(p => p.category))],
    []
  );
  const visibleProducts = useMemo(
    () => (activeCategory === 'all' ? PRODUCTS : PRODUCTS.filter(p => p.category === activeCategory)),
    [activeCategory]
  );

  const handleSelectProduct = useCallback((product) => {
    setSelectedProductId(product.id);
    setSize(product.hasSizes ? product.sizes[0] : null);
    setQuantity(1);
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
    setErrors(prev => {
      if (!prev[name]) return prev;
      const n = { ...prev }; delete n[name]; return n;
    });
  }, []);

  const adjustQuantity = useCallback((delta) => {
    setQuantity(q => Math.min(20, Math.max(1, q + delta)));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (fields.honeypot) return;
    const errs = validate(fields, size, selectedProduct.hasSizes);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setStatus('submitting');
    const ref = makeOrderRef();
    const total = selectedProduct.price * quantity;

    try {
      await addDoc(collection(db, 'merchOrders'), {
        orderReference: ref,
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        size: size || 'One Size',
        color: selectedProduct.color,
        quantity,
        unitPrice: selectedProduct.price,
        totalAmount: total,
        fullName: fields.fullName,
        phone: fields.phone,
        email: fields.email,
        deliveryAddress: fields.deliveryAddress,
        notes: fields.notes,
        status: 'pending_payment',
        createdAt: serverTimestamp(),
      });
      setOrderRef(ref);
      setTotalAmount(total);
      setStatus('success');
    } catch (error) {
      console.error(error);
      alert('Failed to submit order. Please try again.');
      setStatus('idle');
    }
  }, [fields, size, quantity, selectedProduct]);

  const handleReset = useCallback(() => {
    setFields(EMPTY);
    setErrors({});
    setStatus('idle');
    setQuantity(1);
  }, []);

  const SelectedIcon = CATEGORY_ICON[selectedProduct.category];

  return (
    <>
      {/* ── Hero ── */}
      <section className="shop-hero" aria-labelledby="shop-hero-heading">
        <svg className="shop-hero__orbit" viewBox="0 0 400 400" aria-hidden="true" focusable="false">
          <ellipse cx="200" cy="200" rx="170" ry="60" />
          <ellipse cx="200" cy="200" rx="170" ry="60" transform="rotate(60 200 200)" />
          <ellipse cx="200" cy="200" rx="170" ry="60" transform="rotate(120 200 200)" />
          <circle cx="200" cy="200" r="7" className="shop-hero__orbit-core" />
        </svg>
        <div className="shop-hero__inner">
          <span className="shop-hero__eyebrow">NSK Gear</span>
          <h1 className="shop-hero__title" id="shop-hero-heading">NSK Merchandise</h1>
          <p className="shop-hero__lead">
            Show your support with official NSK apparel. All proceeds go
            toward NSK programs and outreach.
          </p>
        </div>
      </section>

      {/* ── Category Filter ── */}
      <div className="shop-filter" role="tablist" aria-label="Filter products by category">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            role="tab"
            aria-selected={activeCategory === cat}
            className={`shop-filter__pill${activeCategory === cat ? ' active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat === 'all' ? 'All' : (CATEGORY_LABELS[cat] || cat)}
          </button>
        ))}
      </div>

      {/* ── Product Grid ── */}
      <section className="shop-products" aria-label="Available products">
        <div className="shop-products__inner">
          {visibleProducts.map((product) => {
            const Icon = CATEGORY_ICON[product.category];
            const accent = COLOR_HEX[product.color] || '#ccc';
            return (
              <div
                className="product-card"
                key={product.id}
                style={{ '--accent': accent }}
              >
                <div className="product-card__media">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="product-card__image"
                  />
                  {Icon && (
                    <span className="product-card__badge" title={CATEGORY_LABELS[product.category] || product.category}>
                      <Icon size={14} aria-hidden="true" />
                    </span>
                  )}
                </div>
                <div className="product-card__body">
                  <h2 className="product-card__title">{product.name}</h2>
                  <p className="product-card__desc">{product.description}</p>
                  <div className="product-card__footer">
                    <span className="product-card__price">KES {product.price.toLocaleString()}</span>
                    <button
                      type="button"
                      className="product-card__btn"
                      onClick={() => handleSelectProduct(product)}
                    >
                      <FaShoppingCart size={13} aria-hidden="true" />
                      Order
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Order Form ── */}
      <section className="shop-order" aria-label="Order form" ref={formRef}>
        <div className="shop-order__inner">
          <div className="shop-order-card">
            {status === 'success' ? (
              <SuccessState orderRef={orderRef} totalAmount={totalAmount} onReset={handleReset} />
            ) : (
              <>
                <h2 className="shop-order-card__heading">Place Your Order</h2>

                <div
                  className="shop-order-preview"
                  style={{ '--accent': COLOR_HEX[selectedProduct.color] || '#ccc' }}
                >
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.name}
                    className="shop-order-preview__image"
                  />
                  <div className="shop-order-preview__details">
                    <span className="shop-order-preview__name">
                      {SelectedIcon && <SelectedIcon size={13} aria-hidden="true" />}
                      {selectedProduct.name}
                    </span>
                    <span className="shop-order-preview__price">
                      KES {selectedProduct.price.toLocaleString()} each
                    </span>
                  </div>
                </div>

                <div className="shopform" role="group" aria-label="Order form">
                  <div className="shopform__honeypot" aria-hidden="true">
                    <label htmlFor="sf-website">Website</label>
                    <input
                      id="sf-website" name="honeypot" type="text"
                      value={fields.honeypot} onChange={handleChange}
                      tabIndex={-1} autoComplete="off"
                    />
                  </div>

                  {/* Product selector */}
                  <div className="shopform__field">
                    <label htmlFor="sf-product" className="shopform__label">Item</label>
                    <select
                      id="sf-product"
                      className="shopform__input"
                      value={selectedProductId}
                      onChange={(e) => {
                        const product = PRODUCTS.find(p => p.id === e.target.value);
                        handleSelectProduct(product);
                      }}
                    >
                      {PRODUCTS.map(p => (
                        <option key={p.id} value={p.id}>{p.name} — KES {p.price.toLocaleString()}</option>
                      ))}
                    </select>
                  </div>

                  {/* Size (conditional) */}
                  {selectedProduct.hasSizes && (
                    <div className="shopform__field">
                      <span className="shopform__label shopform__label--required">Size</span>
                      <div className="shopform__size-group">
                        {selectedProduct.sizes.map((s) => (
                          <button
                            type="button"
                            key={s}
                            className={`shopform__size-btn${size === s ? ' active' : ''}`}
                            onClick={() => setSize(s)}
                            aria-pressed={size === s}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                      {errors.size && <span className="shopform__error" role="alert">{errors.size}</span>}
                    </div>
                  )}

                  {/* Quantity */}
                  <div className="shopform__field">
                    <span className="shopform__label">Quantity</span>
                    <div className="shopform__stepper">
                      <button
                        type="button"
                        className="shopform__stepper-btn"
                        onClick={() => adjustQuantity(-1)}
                        aria-label="Decrease quantity"
                        disabled={quantity <= 1}
                      >
                        <FaMinus size={11} />
                      </button>
                      <span className="shopform__stepper-value" aria-live="polite">{quantity}</span>
                      <button
                        type="button"
                        className="shopform__stepper-btn"
                        onClick={() => adjustQuantity(1)}
                        aria-label="Increase quantity"
                        disabled={quantity >= 20}
                      >
                        <FaPlus size={11} />
                      </button>
                    </div>
                  </div>

                  <p className="jform__divider" style={{ marginTop: 0 }}>Delivery Details</p>

                  <div className="shopform__row">
                    <div className="shopform__field">
                      <label htmlFor="sf-fullName" className="shopform__label shopform__label--required">
                        Full Name
                      </label>
                      <input
                        id="sf-fullName" name="fullName" type="text"
                        className={`shopform__input${errors.fullName ? ' error' : ''}`}
                        value={fields.fullName} onChange={handleChange}
                      />
                      {errors.fullName && <span className="shopform__error" role="alert">{errors.fullName}</span>}
                    </div>
                    <div className="shopform__field">
                      <label htmlFor="sf-phone" className="shopform__label shopform__label--required">
                        Phone Number
                      </label>
                      <input
                        id="sf-phone" name="phone" type="tel"
                        className={`shopform__input${errors.phone ? ' error' : ''}`}
                        value={fields.phone} onChange={handleChange}
                        placeholder="+254 700 000 000"
                      />
                      {errors.phone && <span className="shopform__error" role="alert">{errors.phone}</span>}
                    </div>
                  </div>

                  <div className="shopform__field">
                    <label htmlFor="sf-email" className="shopform__label shopform__label--required">
                      Email Address
                    </label>
                    <input
                      id="sf-email" name="email" type="email"
                      className={`shopform__input${errors.email ? ' error' : ''}`}
                      value={fields.email} onChange={handleChange}
                    />
                    {errors.email && <span className="shopform__error" role="alert">{errors.email}</span>}
                  </div>

                  <div className="shopform__field">
                    <label htmlFor="sf-address" className="shopform__label shopform__label--required">
                      Delivery Address
                    </label>
                    <textarea
                      id="sf-address" name="deliveryAddress" rows={3}
                      className={`shopform__textarea${errors.deliveryAddress ? ' error' : ''}`}
                      value={fields.deliveryAddress} onChange={handleChange}
                      placeholder="Estate/building, street, town/city"
                    />
                    {errors.deliveryAddress && <span className="shopform__error" role="alert">{errors.deliveryAddress}</span>}
                  </div>

                  <div className="shopform__field">
                    <label htmlFor="sf-notes" className="shopform__label">Notes (optional)</label>
                    <textarea
                      id="sf-notes" name="notes" rows={2}
                      className="shopform__textarea"
                      value={fields.notes} onChange={handleChange}
                      placeholder="Anything else we should know?"
                    />
                  </div>

                  <div className="shopform__total">
                    <span>Total</span>
                    <strong>KES {(selectedProduct.price * quantity).toLocaleString()}</strong>
                  </div>

                  <div className="shopform__submit-row">
                    <p className="shopform__privacy">
                      Payment instructions will be shown after you submit.
                    </p>
                    <button
                      type="button" className="shopform__submit"
                      onClick={handleSubmit}
                      disabled={status === 'submitting'}
                      aria-busy={status === 'submitting'}
                    >
                      {status === 'submitting' ? (
                        <><FaSpinner size={14} style={{ animation: 'spin 1s linear infinite' }} /> Submitting…</>
                      ) : (
                        <><FaShoppingCart size={14} /> Place Order</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default Shop;